/**
 * Communication bridge for when the site is embedded in an iframe.
 * Delegates geolocation to the parent page and sends height updates.
 */

const isIframe = window.self !== window.top;

/** Request geolocation — delegates to parent window when in iframe, falls back to native API */
export function requestGeolocation(
  onSuccess: (coords: { latitude: number; longitude: number }) => void,
  onError: (message: string) => void,
): void {
  if (isIframe) {
    const handler = (event: MessageEvent) => {
      if (event.data?.name !== "geolocation") return;
      window.removeEventListener("message", handler);
      if (event.data.data.error) {
        onError(event.data.data.error);
      } else {
        onSuccess(event.data.data);
      }
    };
    window.addEventListener("message", handler);
    window.parent.postMessage({ name: "geolocation" }, "*");
  } else {
    if (!navigator.geolocation) {
      onError("Naršyklė nepalaiko vietos nustatymo");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => onSuccess({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      (err) => {
        const msgs: Record<number, string> = {
          1: "Vietos prieiga nesuteikta",
          2: "Nepavyko nustatyti vietos",
          3: "Vietos nustatymo laikas baigėsi",
        };
        onError(msgs[err.code] || "Nepavyko nustatyti vietos");
      },
      { enableHighAccuracy: false, timeout: 10000 },
    );
  }
}

type TrackType = "widget" | "page";

/** Send analytics event to parent (15min.lt). No-op outside iframe. */
export function trackEvent(type: TrackType, action: string): void {
  if (!isIframe) return;
  window.parent.postMessage({ name: "track", data: { type, action } }, "https://www.15min.lt");
}

/** Map fuel type to tracking action name */
export function fuelAction(fuel: string): string {
  if (fuel === "p95") return "filter_petrol";
  if (fuel === "diesel") return "filter_diesel";
  if (fuel === "lpg") return "filter_lpg";
  return `filter_${fuel}`;
}

/** Observe document height and notify parent iframe when it changes */
export function startHeightSync(): (() => void) | undefined {
  if (!isIframe) return undefined;

  let lastHeight = 0;
  const send = () => {
    const h = document.documentElement.scrollHeight;
    if (h !== lastHeight) {
      lastHeight = h;
      window.parent.postMessage({ name: "set-height", data: { height: h } }, "*");
    }
  };

  // Send initial height
  send();

  const ro = new ResizeObserver(send);
  ro.observe(document.documentElement);

  return () => ro.disconnect();
}
